import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { InsertProduct, UpdateProduct } from "../../../connection/products";
import { UploadFile } from "../../../connection/uploads";
import { addProduct, clearProductsError, deleteProducts, loadProducts, updateProduct } from "../../../store/actions/admin/products";
import { EventsState } from "../../../store/types";
import { GetDefaultProduct } from "../../../store/utils/create-event";
import { Attribute, CreateProduct, LanguagesAbbr, Product } from "../../../types/working-model";
import { capitalize, updateTranslateKey } from "../../../utils/utils";
import Icon, { ICONS, COLORS } from "../../general-ui/icon";
import EnterSelectInput from "../../general-ui/select/enter-select";
import Switch from "../../general-ui/switch/switch";
import TagSelectInput from "../../general-ui/tag-select/tag-select";
import TextInput, { Validation } from "../../general-ui/text-input/text";
import Textarea from "../../general-ui/textarea/textarea";
import ModalComponent from "../../general-ui/modal/modal";
import WaitingIndicator from "../../general-ui/waiting-indicator/waiting-indicator";
import AddProduct from "../create/homepage/editor/products/add-product";
import { VisibleProduct } from "../create/homepage/editor/products/products";
import { useCallback } from "react";
import classNames from "classnames";
import { Tooltip } from "../../general-ui/tooltip/tooltip";

export type WorkingProductFields = "button_text" | "product_title" | "description" | "price" | "sale_price"

interface Props {
	title: string;
	open: boolean;
	close: () => void;
	onComplete: (products: Product[]) => void;
	initialSelected: Product[];
	productToEdit: Product | null;
	replacing?: null | Product | undefined;
	language: LanguagesAbbr;
	baseLanguage: LanguagesAbbr;
}

const initialRequiredFields: { [key: string]: Validation } = {
	title: Validation.normal,
	about: Validation.normal,
	image: Validation.normal
};

const CreateProductModal: React.FC<Props> = ({ title, open, close, onComplete, initialSelected, productToEdit, replacing, language, baseLanguage }) => {
	const dispatch = useDispatch();
	const {
		ProductsReducer: {
			products,
			productsError
		},
		AuthReducer: {
			token,
			user
		},
		CreateSessionReducer: {
			workingSession
		},
		CreateEventReducer: {
			workingEvent
		}
	} = useSelector((state: EventsState) => state);

	const [selectedProducts, setSelectedProducts] = useState<number[]>(initialSelected.map(p => p.product!));
	const [uploading, setUploading] = useState(false);
	const [workingProduct, setWorkingProduct] = useState<CreateProduct | Product | null>(null);
	const [searchTerm, setSearchTerm] = useState("");
	const [productToReplace, setProductToReplace] = useState<Product | null | undefined>(replacing);
	const [saving, setSaving] = useState(false);
	const imageRef = useRef<HTMLInputElement | null>(null);
	const [requiredFields, setRequiredFields] = useState(initialRequiredFields);

	useEffect(() => {
		setWorkingProduct(productToEdit);
	}, [productToEdit, close]);

	useEffect(() => {
		setProductToReplace(replacing);
	}, [replacing]);

	useEffect(() => {
		setSelectedProducts(initialSelected.map(p => p.product!));
	}, [initialSelected]);

	async function finishedAddingProduct() {
		setSaving(true);
		if (!workingProduct) {
			const fullProductsOrdered = selectedProducts.map((productId: number) => products.find((_product: Product) => _product.product! === productId));

			onComplete(fullProductsOrdered as Product[]);
			close();
		} else {
			// if editing/creating product
			if (!hasCompletedRequiredFields()) return;
			const _product = { ...workingProduct };

			//remove empty attributes
			_product.attributes = _product.attributes?.filter(
				(attribute: Attribute) => attribute.type.trim().length && attribute.value.trim().length
			) ?? [];

			//we're editing a product, not creating a new one
			if ("product" in _product) {
				// if editing product from parent, update and close the whole modal
				if (productToEdit) {
					const updatedModules = initialSelected.map(p => {
						if (p.product === _product.product) {
							return _product;
						}
						return p;
					});
					await UpdateProduct(token!, _product, workingSession?.languages);
					onComplete(updatedModules);
					close();
				} else {
					const updated = await UpdateProduct(token!, _product, workingSession?.languages);
					dispatch(updateProduct(updated));
				}
			} else {
				const newProduct = await InsertProduct(token!, _product, workingSession?.languages);
				dispatch(addProduct(newProduct));
			}
			setWorkingProduct(null);
		}
		setSaving(false);
	}

	function hasCompletedRequiredFields() {
		const _requiredFields = { ...requiredFields };
		if (!workingProduct?.product_title?.base) _requiredFields.title = Validation.error;
		if (!workingProduct?.description?.base) _requiredFields.about = Validation.error;
		if (!workingProduct?.images.length) _requiredFields.image = Validation.error;

		setRequiredFields(_requiredFields);
		for (const field in _requiredFields) if (_requiredFields[field] === Validation.error) return false;
		return true;
	}

	function resetRequiredFields() {
		const _requiredFields = { ...requiredFields };
		for (const field in _requiredFields) _requiredFields[field] = Validation.normal;
		setRequiredFields(_requiredFields);
	}

	function cancel() {
		if (workingProduct) {
			if (productToEdit) return close();
			resetRequiredFields();
			setWorkingProduct(null);
		} else {
			// when closing, reset replacing so that the last product to get replaced isnt stored
			setProductToReplace(replacing);

			close();
		}
		setSearchTerm('');
	}

	const modalTitle = useMemo(() => {
		// display "replace product" as modal title when replacing button is clicked
		if (replacing) return "Replace product";
		if (!workingProduct) {
			return title;
		} else if ("product" in workingProduct) {
			return "Edit Product";
		} else {
			return "Add New Product";
		}
	}, [replacing, workingProduct, title]);

	async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];

		if (file) {
			setUploading(true);
			const uploaded = await UploadFile(user!, token!, file);
			setWorkingProduct((product: Product | CreateProduct | null) => {
				if (!product) return null;
				setRequiredFields({ ...requiredFields, image: uploaded ? Validation.normal : Validation.error });
				return {
					...product,
					images: [...product.images, uploaded]
				};
			});
			setUploading(false);
		}
	}

	function removeImage(index: number) {
		return () => {
			setWorkingProduct((workingProduct: Product | CreateProduct | null) => {
				if (!workingProduct) { return null; }

				return {
					...workingProduct,
					images: workingProduct.images.filter((img: string, _index: number) => _index !== index)
				};
			});
		};
	}

	function handleSearch({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
		setSearchTerm(value);
	}

	function toggleProduct(product: VisibleProduct, isOn: boolean) {
		let selectedModules = [...selectedProducts];

		if (replacing) {
			// if clicking a product that is the replacing product, or is already in the selected products, dont do anything
			if (selectedModules.includes(product.product!) || replacing.product === product.product) return;

			// if admin clicks more than 1 product when replacing, only replace with the most recent product
			const lastProduct = productToReplace || replacing;
			const startIndex = selectedModules.findIndex((moduleId: number) => moduleId === lastProduct.product);
			selectedModules.splice(startIndex, 1, product.product!);

			setProductToReplace(product);
			setSelectedProducts(selectedModules);

			return;
		}

		if (isOn) {
			UpdateProduct(token!, product, workingSession ? workingSession.languages : workingEvent!.homepage!.languages).then(result => {
				dispatch(updateProduct(result));
			});
			selectedModules = [...selectedModules, product.product!];
		} else {
			selectedModules = selectedModules.filter((module: number) => module !== product.product);
		}

		setSelectedProducts(selectedModules);
	}

	function editProduct(product: Product) {
		setWorkingProduct(product);
	}

	function deleteProduct(product: Product) {
		if (product?.product && user?.active_channel) {
			dispatch(deleteProducts([Number(product.product)], token!, user?.active_channel));
		}
	}

	function setProductTitle({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
		updateProductTransKey('product_title', value);
		setRequiredFields({ ...requiredFields, title: value ? Validation.normal : Validation.error });
	}

	function setBuyNowTitle({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
		updateProductTransKey('button_text', value);
	}

	function setDescription({ target: { value } }: React.ChangeEvent<HTMLTextAreaElement>) {
		updateProductTransKey('description', value);
		setRequiredFields({ ...requiredFields, about: value ? Validation.normal : Validation.error });
	}

	function setPrice({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
		if (maxDecimalPlaces(value)) return;
		updateProductTransKey('price', value);
	}

	function setSalePrice({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
		if (maxDecimalPlaces(value)) return;
		updateProductTransKey('sale_price', value);
	}

	function maxDecimalPlaces(value: string) { return value.includes('.') && value.split('.')[1].length > 2; }

	function formatCurrency(key: WorkingProductFields, { target: { valueAsNumber, value } }: React.ChangeEvent<HTMLInputElement>) {
		// toLocalString to handle rounding/formatting
		if (value.length) {
			const localValue = valueAsNumber.toLocaleString('us', { style: 'currency', currency: 'USD' });
			const formatAsNumber = localValue.substring(1).replaceAll(',', '');
			updateProductTransKey(key, formatAsNumber);
		} else {
			updateProductTransKey(key, '');
		}
	}

	function updateProductTransKey(key: WorkingProductFields, value: string) {
		if (!workingProduct) { return; }

		const _workingProduct = { ...workingProduct };

		setWorkingProduct(workingProduct => {
			if (!workingProduct) return null;

			return {
				...workingProduct,
				[key]: updateTranslateKey({
					translateString: _workingProduct[key],
					input: value,
					baseLanguage,
					language
				})
			};
		});
	}

	function setUseAttributes(value: any, isOn: boolean) {
		setWorkingProduct((product: Product | CreateProduct | null) => {
			if (!product) { return null; }

			if (isOn) {
				if (!product.attributes || product.attributes.length < 1) {
					return {
						...product,
						use_attributes: true,
						attributes: [{
							type: "",
							value: ""
						}]
					};
				} else {
					return {
						...product,
						use_attributes: true
					};
				}
			} else {
				return {
					...product,
					use_attributes: false
				};
			}
		});
	}

	function setCategories(categories: string[]) {
		setWorkingProduct((product: Product | CreateProduct | null) => {
			if (!product) { return null; }

			return {
				...product,
				categories: categories
			};
		});
	}

	function setAttributeName(index: number) {
		return (attribute: string) => {
			setWorkingProduct((workingProduct: Product | CreateProduct | null) => {
				if (!workingProduct) { return null; }
				return {
					...workingProduct,
					attributes: workingProduct.attributes.map((_attribute: any, _index: number) => {
						if (_index === index) {
							_attribute.type = attribute;
						}

						return _attribute;
					})
				};
			});
		};
	}

	function setAttributeValue(index: number) {
		return ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
			setWorkingProduct((workingProduct: Product | CreateProduct | null) => {
				if (!workingProduct) { return null; }

				return {
					...workingProduct,
					attributes: workingProduct.attributes.map((_attribute: any, _index: number) => {
						if (_index === index) {
							_attribute.value = value;
						}

						return _attribute;
					})
				};
			});
		};
	}

	function addNewAttribute() {
		setWorkingProduct((workingProduct: Product | CreateProduct | null) => {
			if (!workingProduct) { return null; }

			return {
				...workingProduct,
				attributes: [...(workingProduct.attributes ?? []), { type: '', value: '' }]
			};
		});
	}

	function createNewProduct() {
		setWorkingProduct(GetDefaultProduct());
	}

	function uploadNewImage() {
		imageRef.current?.click();
	}

	function setBuyNowLink({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
		setWorkingProduct((product: Product | CreateProduct | null) => {
			if (!product) { return null; }

			return {
				...product,
				buy_now_link: value
			};
		});
	}

	useEffect(() => {
		dispatch(loadProducts(token!, user!.active_channel));
	}, [dispatch, token, user]);

	const sortedAttributesList = useMemo(() => {
		const attributeTypes = new Set<string>();
		for (const product of products) {
			product.attributes?.forEach((attribute: Attribute) => { attributeTypes.add(capitalize(attribute.type)); });
		}

		// sorting in alphabetical order
		return Array.from(attributeTypes).sort();
	}, [products]);

	const visibleProducts = useMemo(() => {
		if (products) {
			const _term = searchTerm.replace(/\s/g, '').trim().toLowerCase();
			return products.filter((product: Product) => {
				return product.product_title.base.replace(/\s/g, '').toLowerCase().trim().startsWith(_term);
			});
		} else {
			return [];
		}
	}, [searchTerm, products]);

	const closeModal = useCallback(() => {
		setProductToReplace(replacing);
		close();
	}, [close, replacing]);

	const closeWarningModal = () => dispatch(clearProductsError());

	return (
		<ModalComponent
			closeable={false}
			cancellable={false}
			open={open}
			onRequestClose={closeModal}
			title={modalTitle}
			footer={(
				<>
					<button onClick={cancel} style={{ marginRight: 16 }}>Cancel</button>
					<button onClick={finishedAddingProduct} className="primary">{saving ? <WaitingIndicator /> : "Done"}</button>
				</>
			)}
		>
			{workingProduct ? (
				<div>
					<div className="row">
						<TextInput
							placeholder={"Enter Product Title"}
							label={"Product Title*"}
							valid={requiredFields.title}
							onBlur={() => {
								setRequiredFields({ ...requiredFields, title: (workingProduct?.product_title?.[language] || workingProduct?.product_title?.base) ? Validation.normal : Validation.error });
							}}
							onChange={setProductTitle}
							defaultValue={workingProduct.product_title?.[language] as string ?? workingProduct.product_title?.base}
							value={workingProduct.product_title?.[language] as string ?? workingProduct.product_title?.base}
						/>
						<TagSelectInput
							onChange={setCategories}
							initialTags={workingProduct.categories ?? []}
							label={"Categories"}
						/>
					</div>
					<Textarea
						placeholder="Describe your product"
						label="About*"
						// Keeping here incase we want to add back in later.
						// make sure to also update the edit-products.tsc modal as well
						onBlur={() => {
							setRequiredFields({ ...requiredFields, about: (workingProduct?.description?.[language] || workingProduct?.description?.base) ? Validation.normal : Validation.error });
						}}
						valid={requiredFields.about}
						defaultValue={workingProduct?.description?.[language] as string ?? workingProduct?.description?.base}
						onChange={setDescription}
					/>
					<div className={classNames('product-images', { error: requiredFields.image === Validation.error })}>
						{workingProduct.images.map((image: string, index: number) => (
							<div className="product-image" key={index} style={{ backgroundImage: `url(${image.replace(/ /g, '%20')})` }}>
								<div className="product-image-actions">
									<button className="no-style" onClick={removeImage(index)}><Icon name={ICONS.TRASH} size={14} color={COLORS.WHITE} /></button>
								</div>
							</div>
						))}
						{uploading && (
							<div className="product-image">
								<WaitingIndicator />
							</div>
						)}
						{workingProduct.images.length < 4 && !uploading && (
							<div className={classNames("upload-container", { empty: !workingProduct.images.length })}>
								<button className="add-image" onClick={uploadNewImage}>
									<Icon name={ICONS.ADD} size={16} color={COLORS.WHITE} />
									<span>Upload Photo</span>
								</button>
								<p>Accepted file types are .png, .jpeg and up to 50 MB file size</p>
							</div>
						)}
						<input type="file" style={{ display: 'none' }} onChange={handleImageUpload} accept=".jpg,.jpeg,.png,.gif" ref={imageRef} />
					</div>
					<div className="row">
						<TextInput
							placeholder={"Enter Product Price"}
							label={"Price"}
							onChange={setPrice}
							onBlur={(e) => formatCurrency('price', e)}
							defaultValue={workingProduct.price?.base ?? ''}
							value={workingProduct.price?.base ?? ''}
							numeric
						/>
						<TextInput
							placeholder={"Enter Product Sale Price"}
							label={"Sale Price"}
							onChange={setSalePrice}
							onBlur={(e) => formatCurrency('sale_price', e)}
							defaultValue={workingProduct.sale_price?.base ?? ''}
							value={workingProduct.sale_price?.base ?? ''}
							numeric
						/>
					</div>
					<div className="row">
						<TextInput
							placeholder={"Enter Product Link URL"}
							label={"Buy Now URL"}
							onChange={setBuyNowLink}
							defaultValue={workingProduct.buy_now_link ?? ''}
						/>
						<TextInput
							placeholder={"Enter Product Link Text"}
							label={"Buy Now Button Name"}
							onChange={setBuyNowTitle}
							defaultValue={workingProduct.button_text?.[language] as string ?? workingProduct.button_text?.base}
						/>
					</div>
					<div className="field-group switch">
						<Switch
							value={'use-attributes'}
							on={workingProduct.use_attributes}
							onClick={setUseAttributes}
						/>
						<label>Product Attributes</label>
					</div>
					{workingProduct.use_attributes && (
						<div className="attributes-list">
							{workingProduct.attributes && workingProduct.attributes.length > 0 && (
								<>
									{workingProduct.attributes.map((attribute: any, index: number) => (
										<div key={index} className="attribute-row">
											<EnterSelectInput
												defaultOptions={sortedAttributesList.length ? sortedAttributesList : ["Color", "Size"]}
												label="Attribute"
												placeholder="Enter attribute"
												onChange={setAttributeName(index)}
												defaultValue={attribute.type}
											/>
											<TextInput
												placeholder={"Attribute value"}
												label={"Value"}
												onChange={setAttributeValue(index)}
												defaultValue={attribute.value}
											/>
										</div>
									))}
								</>
							)}

							<button onClick={addNewAttribute} className="no-style">
								<Icon name={ICONS.ADD} size={14} color={COLORS.WHITE} />
								<span>Add Attribute</span>
							</button>
						</div>
					)}
				</div>
			) : (
				<div className="select-products">
					<div className="search-products">
						<TextInput
							onChange={handleSearch}
							placeholder={"Search Products..."}
							icon={ICONS.SEARCH}
							size={"small"}
							defaultValue={searchTerm}
							updateValue={searchTerm}
						/>
						<p className="middle">or</p>
						<button className="button small" disabled={language !== baseLanguage} onClick={createNewProduct}>
							Create Product
						</button>
						{language !== baseLanguage && (
							<div className="product-tooltip">
								<Tooltip tooltip="Products may only be created in the base language">
									<Icon name={ICONS.PRIMARY_TOOLTIP} color={COLORS.CYAN} size={12} />
								</Tooltip>
							</div>
						)}
					</div>
					<div className="products-list">
						{visibleProducts.map((product: VisibleProduct) => (
							<AddProduct
								selected={selectedProducts}
								onClick={toggleProduct}
								product={product}
								replacing={productToReplace}
								editProduct={editProduct}
								key={product.product}
								deleteProduct={deleteProduct}
							/>
						))}
					</div>
					<ModalComponent
						title="Could not delete"
						open={!!productsError}
						onRequestClose={closeWarningModal}
						closeable
						cancellable
					>
						{/* <p>TODO: change to warning modal</p> */}
						<p>The following product(s) must be removed from all event homepages and sessions before being deleted: </p><em>{productsError}</em>
					</ModalComponent>
				</div>
			)
			}
		</ModalComponent >
	);
};

export default CreateProductModal;
